/**
 * Search API client.
 *
 * One typed fetch wrapper around POST /api/search. Used by:
 *   - the comparison page Server Component (server-side fetch on render)
 *   - any client-side action that needs a fresh search
 */

import { searchIdentityHeaders } from "@/lib/anon-session";
import { bearerHeaders } from "@/lib/auth-bearer";

import { getInternalAuthHeaders } from "./internal-headers";
import type {
  SearchByQueryRequest,
  SearchByQueryResponse,
  SearchRequest,
  SearchResponse,
} from "./types";

/**
 * Resolve the API base URL.
 *
 * On the server (RSC, Route Handlers) we read API_URL or fall back to
 * the local Docker default. On the client we read NEXT_PUBLIC_API_URL
 * for the public address (CORS-allowed).
 */
function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return process.env.API_INTERNAL_URL ?? process.env.API_URL ?? "http://localhost:8000";
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export class SearchError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

/** Sentinel status values used by callers to render the right UI. */
export const SEARCH_STATUS_NETWORK = 0;
export const SEARCH_STATUS_TIMEOUT = -1;

// Server-side SSR fetch timeout for /api/search/by-query. Search
// runs broad SERP + per-retailer fan-out + 12-20 retailer page
// fetches + 12-20 LLM extracts in parallel. Wall-clock budget on a
// cold cache miss is ~30-60s for popular queries, occasionally up
// to 90s when many retailers need render=true. 120s gives healthy
// headroom without leaving the user staring forever.
const SSR_SEARCH_TIMEOUT_MS = 120_000;

export async function postSearch(req: SearchRequest): Promise<SearchResponse> {
  const url = `${apiBaseUrl()}/api/search`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // postSearch is called from the /c/[slug] Server Component (SSR
      // only); getInternalAuthHeaders is empty on the browser so the
      // secret never leaks even if a future client path adopts this
      // helper by accident.
      ...getInternalAuthHeaders(),
    },
    body: JSON.stringify(req),
    cache: "no-store", // search results vary per request; leave caching to ISR upstream
  });

  if (!response.ok) {
    let detail: unknown = null;
    try {
      detail = await response.json();
    } catch {
      detail = await response.text();
    }
    throw new SearchError(
      response.status,
      detail,
      `POST /api/search returned ${response.status}`,
    );
  }
  return (await response.json()) as SearchResponse;
}

/**
 * D46: natural-language entry point. Backend runs Phase A
 * (parse_intent → score_retailers → discover_url) then the same
 * SearchOrchestrator + TFP enrichment as `postSearch` and returns
 * both the parsed intent and the comparison result in one trip.
 */
export async function postSearchByQuery(
  req: SearchByQueryRequest,
  userId?: string | null,
): Promise<SearchByQueryResponse> {
  const url = `${apiBaseUrl()}/api/search/by-query`;

  // 2026-05-13 hotfix: explicit AbortController so the SSR fetch
  // has a known, predictable timeout. Without this, behaviour
  // depends on undici/Node version defaults and was inconsistent
  // in prod (a 59s Levi's search threw with no clear cause and the
  // user saw the misleading "running locally?" error).
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    SSR_SEARCH_TIMEOUT_MS,
  );

  // 2026-06-01: x-turnstile-token header removed with Cloudflare
  // Turnstile end-to-end teardown. SearchSecurityMiddleware no
  // longer requires it. Pre-launch B-098 sign-up wall is the next
  // anti-abuse layer.
  //
  // 2026-06-07 (hardcoded-pages-recovery): include the internal-auth
  // shared secret so SSR callers (the /c/[slug] Server Component
  // calling by-query for natural-language discovery) bypass
  // SearchSecurityMiddleware's origin check, matching the bypass
  // pattern postSearch has used since 2026-05-30. The helper returns
  // {} on the browser side (typeof window !== "undefined") so this
  // does NOT leak the secret to client bundles; ComparisonStream's
  // browser-side fallback caller is unaffected.
  // B-098: attach the searcher's identity (Clerk user id + anon sticker)
  // so the fallback path counts against the same wall as the stream. On
  // the server searchIdentityHeaders returns {} (no breakage for any SSR
  // caller); this function is browser-only today (ComparisonStream).
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...getInternalAuthHeaders(),
    ...searchIdentityHeaders(userId),
    // 2026-08-22: Clerk Bearer for the browser fallback caller (ComparisonStream)
    // so a signed-in user passes the require-sign-in gate. Returns {} on SSR
    // (which uses the internal-auth bypass above) and when signed out.
    ...(await bearerHeaders()),
  };

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(req),
      cache: "no-store",
      signal: controller.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      throw new SearchError(
        SEARCH_STATUS_TIMEOUT,
        null,
        `POST /api/search/by-query timed out after ${SSR_SEARCH_TIMEOUT_MS}ms`,
      );
    }
    throw new SearchError(
      SEARCH_STATUS_NETWORK,
      null,
      `POST /api/search/by-query network error: ${(e as Error).message}`,
    );
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let detail: unknown = null;
    try {
      detail = await response.json();
    } catch {
      detail = await response.text();
    }
    throw new SearchError(
      response.status,
      detail,
      `POST /api/search/by-query returned ${response.status}`,
    );
  }
  return (await response.json()) as SearchByQueryResponse;
}
